import {
  Box,
  CloseButton,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputProps,
  Popover,
  PopoverContentProps,
  Spinner,
  Text,
  useDisclosure,
  Presence,
} from '@chakra-ui/react';
import { Tooltip } from '../../ui/tooltip';
import React, {
  FunctionComponent as FC,
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MdSearch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { ApiDataMapKey } from '../../../api/queryUtils';
import useBoolean from '../../../hooks/useBoolean';
import { useDebounce } from '../../../hooks/useDebounce';
import { useGetOmniSearchList } from '../../../hooks/useGetOmniSearchList';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { sharedFieldBase } from '../../../styles/chakra/components/Input';
import { pluralize } from '../../../utils';
import { buildRecordRoute } from '../../../utils/recordIds';
import { MBackButton } from '../MBackButton';
import { useCursor } from '../MCustomSelect/components/useCursor';
import { SearchedList } from '../MPageSearchInput/SearchedList';
import { useKeyPress } from '../MPageSearchInput/useKeyPress';
import MText from '../MText';
import { MDivider, MStack } from '../chakra';

interface OmniSearchInputProps {
  apiKey: ApiDataMapKey;
  /** Query parameter to search by */
  searchKey: string;
  /** Field to use as the name property in the list, defaults to searchKey */
  nameField?: string;
  /** Sort Field to use, defaults to nameField */
  sortField?: string;
  value: string;
  popoverContentProps?: PopoverContentProps;
  searchTooltip?: string;
  placeholder?: string;
  /** If true, the input will not collapse to an icon */
  alwaysExpanded?: boolean;
  borderRadius?: InputProps['borderRadius'];
  size?: InputProps['size'];
  leftElementIcon?: number;
  border?: InputProps['border'];
  backgroundColor?: string;
  focusBackgroundColor?: string;
  clearable?: boolean;
  fontSize?: InputProps['fontSize'];
  focusColor?: string;
  borderTop?: InputProps['borderTop'];
  containerClicked?: boolean;
  /** Called when value changes or form is submitted */
  onChange: (val: string, isSubmitted?: boolean) => void;
  renderViewHeader?: ({
    onToggle,
  }: {
    onToggle: () => void;
  }) => React.ReactNode;
  onReset?: () => void;
}

export const OmniSearchInput: FC<OmniSearchInputProps> = React.forwardRef<
  HTMLInputElement,
  OmniSearchInputProps
>(
  (
    {
      value,
      apiKey,
      searchKey,
      nameField = searchKey,
      sortField = nameField,
      searchTooltip = 'Search',
      placeholder = 'Search...',
      popoverContentProps = {},
      alwaysExpanded = false,
      borderRadius = 4,
      size = 'sm',
      border,
      fontSize = 'sm',
      borderTop,
      backgroundColor = sharedFieldBase.bg,
      focusBackgroundColor = sharedFieldBase._focus.bg,
      clearable = false,
      focusColor = sharedFieldBase._focus.color,
      containerClicked = false,
      renderViewHeader,
      onChange,
      onReset,
    }: OmniSearchInputProps,
    _ref,
  ) => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const itemRefs = useRef<{ [index: number]: HTMLAnchorElement }>({});
    const escapePressed = useKeyPress('Escape');

    const [internalValue, setInternalValue] = useState<string>(value);
    const debouncedValue = useDebounce(internalValue);
    const [isSubmitted, setIsSubmitted] = useBoolean(!!value);

    const {
      open: isOpen,
      onClose,
      onToggle,
    } = useDisclosure({
      defaultOpen: !!value || !!alwaysExpanded,
      onOpen: () => {
        setCursor(-1);
        // Using timeout to make sure the animation is complete
        setTimeout(() => inputRef.current?.focus());
      },
    });

    // Use the new omni search hook
    const { data, isFetching, fieldConfig } = useGetOmniSearchList({
      debouncedValue,
      apiKey,
      sortField,
      isOpen,
    });

    // Keeps track of the searched list item current cursor index
    const { cursor, setCursor, setCursorAndScroll } = useCursor({
      isItemSelectable: () => true,
      items: data?.content || [],
      itemRefs,
      isDisabledItem: () => false,
      resultListRef: null,
    });

    useOutsideClick({
      ref: containerRef,
      handler: () => {
        if (isOpen && !internalValue) {
          !alwaysExpanded && onClose();
          setIsSubmitted.off();
          handleReset();
        }
        if (!isSubmitted && isOpen && !!internalValue) {
          !alwaysExpanded && onClose();
          setInternalValue('');
        }
      },
    });

    const onSubmit = (ev: FormEvent<HTMLFormElement>) => {
      ev.preventDefault();

      // If there's exactly one result, navigate to it
      const isSingleItem: boolean = data?.totalElements === 1;

      if (isSingleItem) {
        setIsSubmitted.on();

        // Route from the record's own ID rather than the typed text: an invoice
        // number search returns an `invc_` record that the input never named.
        if (data?.content[0]) {
          const { id } = data.content[0] as { id: string };
          navigate(buildRecordRoute(id), { replace: true });
        }
        handleReset();
        return;
      }

      // If multiple results or no exact matches, let parent handle the submission
      setIsSubmitted.on();
      onChange(internalValue, true);
    };

    const handleReset = useCallback(() => {
      onReset?.();
      setInternalValue('');
      onChange('');
      !alwaysExpanded && onToggle();
    }, [onReset, onChange, alwaysExpanded, onToggle]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isSubmitted) {
        setIsSubmitted.off();
      }
      const newValue = event.target.value;
      setInternalValue(newValue);
    };

    useEffect(() => {
      if (escapePressed && !isSubmitted && isOpen) {
        handleReset();
      }
    }, [escapePressed, isOpen, isSubmitted, handleReset]);

    useEffect(() => {
      if (containerClicked) {
        inputRef.current?.focus();
      }
    }, [containerClicked]);

    // Always show search results - no special entity ID hints
    const showPopover = isOpen && !!debouncedValue && !isSubmitted;
    const showSearchResults = data?.content.length;

    const resultsTemplate =
      data?.totalElements && data.totalElements > 20
        ? `of ${data.totalElements}`
        : pluralize('result', data?.totalElements || 0);

    return (
      <>
        {!isOpen && !renderViewHeader && (
          <Tooltip
            content={searchTooltip}
            positioning={{
              placement: 'bottom-end',
            }}
          >
            <IconButton
              variant="icon"
              minW={8}
              aria-label={placeholder}
              onClick={onToggle}
            >
              <Icon w={5} h={5} asChild>
                <MdSearch />
              </Icon>
            </IconButton>
          </Tooltip>
        )}
        {!isOpen && renderViewHeader?.({ onToggle })}
        {isOpen && (
          <Box
            w="100%"
            h="100%"
            ref={containerRef}
            position="relative"
            alignContent={'center'}
          >
            <WithOptionalScaleFade
              isOpen={isOpen}
              enableAnimation={!alwaysExpanded}
            >
              <form onSubmit={onSubmit}>
                <Flex align="center">
                  {renderViewHeader && <MBackButton onClick={handleReset} />}
                  <InputGroup
                    borderColor="tBlue.lightShade"
                    minWidth="21.25rem"
                    startElement={
                      <Icon w={5} h={5} color="tPurple.base" asChild>
                        <MdSearch />
                      </Icon>
                    }
                    endElement={
                      clearable &&
                      !!internalValue && (
                        <CloseButton
                          size="sm"
                          color="tPurple.base"
                          onClick={handleReset}
                        />
                      )
                    }
                  >
                    <Input
                      borderRadius={borderRadius}
                      size={size}
                      {...(border ? { border } : {})}
                      fontSize={fontSize}
                      value={internalValue}
                      onChange={handleInputChange}
                      ref={inputRef}
                      backgroundColor={backgroundColor}
                      color="tPurple.base"
                      placeholder={placeholder}
                      _focus={{
                        ...sharedFieldBase._focus,
                        backgroundColor: focusBackgroundColor,
                        color: focusColor,
                      }}
                      onFocus={() => {
                        if (isSubmitted && debouncedValue) {
                          setIsSubmitted.off();
                        }
                      }}
                    />
                  </InputGroup>
                </Flex>
              </form>
            </WithOptionalScaleFade>

            <Popover.Root
              lazyMount
              unmountOnExit
              open={showPopover}
              autoFocus={false}
              onOpenChange={(e) => {
                if (e.open) {
                } else {
                  !alwaysExpanded && onClose();
                }
              }}
              positioning={{
                offset: { mainAxis: 0, crossAxis: 0 },
                placement: 'bottom-start',
              }}
            >
              <Popover.Trigger asChild>
                <Box />
              </Popover.Trigger>
              <Popover.Positioner>
                <Popover.Content
                  maxW="40rem"
                  minW="21.25rem"
                  width="fit-content"
                  // Shadow inherits from Popover theme. Border zeroed because
                  // this dropdown sits flush under the search input; caller
                  // controls the seam via borderTop / borderRadius.
                  border="0"
                  borderTop={borderTop}
                  borderRadius={borderRadius}
                  {...popoverContentProps}
                >
                  <Popover.Body p={2}>
                    {!isFetching && !showSearchResults && (
                      <Text textAlign="center">No search results found</Text>
                    )}

                    {!!isFetching && (
                      <Flex align="center" justify="center">
                        <Spinner />
                      </Flex>
                    )}

                    {!!showSearchResults && !isFetching && internalValue && (
                      <MStack>
                        <MText fontWeight="bold" fontSize={'sm'}>
                          {fieldConfig.entityDisplayName}
                        </MText>
                        <MDivider />
                        <SearchedList
                          itemRefs={itemRefs}
                          debouncedValue={debouncedValue}
                          nameField={fieldConfig.nameField}
                          isFetching={isFetching}
                          data={data as any}
                          cursor={cursor}
                          setCursorAndScroll={setCursorAndScroll}
                          handleReset={handleReset}
                          generateRoute={buildRecordRoute}
                        />
                      </MStack>
                    )}
                  </Popover.Body>

                  {!!showSearchResults && !isFetching && (
                    <Popover.Footer mx="0" py="2" px="4">
                      <Text
                        fontSize="xs"
                        fontWeight="400"
                        textAlign="center"
                      >{`Showing ${data?.content.length} ${resultsTemplate}`}</Text>
                    </Popover.Footer>
                  )}
                </Popover.Content>
              </Popover.Positioner>
            </Popover.Root>
          </Box>
        )}
      </>
    );
  },
);

const WithOptionalScaleFade = ({
  enableAnimation,
  isOpen,
  children,
}: {
  enableAnimation: boolean;
  isOpen: boolean;
  children: ReactNode;
}) => {
  if (enableAnimation) {
    return (
      <Presence
        present={isOpen}
        animationStyle={{
          _open: 'scale-fade-in',
          _closed: 'scale-fade-out',
        }}
        animationDuration="moderate"
      >
        {children}
      </Presence>
    );
  }

  return <>{children}</>;
};
